/**
 * Zippy WMS — thermal printer client for Xprinter XP-P322B (and other
 * generic ESC/POS BLE printers).
 *
 * Two paths:
 *   1. Web Bluetooth  → direct BLE connection, one-tap print on Chrome/Android.
 *   2. Browser print  → `window.print()` with a `@page { size: 80mm 40mm }`
 *                       stylesheet, works everywhere (iOS Safari, desktop).
 *
 * The XP-P322B is an 80mm portable thermal printer that advertises the
 * common ESC/POS BLE service `000018f0-0000-1000-8000-00805f9b34fb` with
 * a single write characteristic `00002af1-...`. It renders EAN-13 barcodes
 * natively via `GS k` so we never send bitmap data.
 */

const PRINTER_SERVICE_UUID = '000018f0-0000-1000-8000-00805f9b34fb';
const PRINTER_WRITE_UUID = '00002af1-0000-1000-8000-00805f9b34fb';
const STORAGE_KEY = 'wms_printer_device_name';
const CHUNK_SIZE = 180; // safe for BLE MTU ~185-247

// ============ ESC/POS COMMAND BUILDER ============

const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;

const bytes = (...arr) => new Uint8Array(arr.flat());

const encodeText = (text) => {
  // Xprinter default codepage is CP437; ASCII/Latin-1 works cleanly for
  // English SKUs / product names. Non-ASCII will be replaced with '?'.
  const clean = String(text || '').replace(/[^\x20-\x7e]/g, '?');
  return new TextEncoder().encode(clean);
};

const concat = (...chunks) => {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) { out.set(c, offset); offset += c.length; }
  return out;
};

/**
 * Build an ESC/POS byte stream for a single label:
 *   [Product name, bold]
 *   [Barcode  EAN-13, HRI below]
 *   [SKU code]
 */
export const buildLabelCommands = ({ name, sku, barcode }) => {
  if (!barcode || !/^\d{12,13}$/.test(barcode)) {
    // For anything other than 12/13-digit numeric we fall back to CODE128
    return buildLabelCode128({ name, sku, barcode });
  }

  const cleanName = truncate(name || '', 28);

  // Some drivers require 12 digits (Function A auto-computes check digit).
  // Function B ("GS k 67 n d...") takes 13 digits + explicit check.
  let barcodeData;
  if (barcode.length === 13) {
    barcodeData = new Uint8Array(13);
    for (let i = 0; i < 13; i++) barcodeData[i] = barcode.charCodeAt(i);
  } else {
    barcodeData = new Uint8Array(12);
    for (let i = 0; i < 12; i++) barcodeData[i] = barcode.charCodeAt(i);
  }

  return concat(
    bytes(ESC, 0x40),                    // ESC @ - init printer
    bytes(ESC, 0x61, 0x01),              // ESC a - center align
    bytes(ESC, 0x21, 0x08),              // ESC ! - emphasize (bold)
    encodeText(cleanName),
    bytes(LF),
    bytes(ESC, 0x21, 0x00),              // ESC ! - normal
    bytes(GS, 0x68, 0x50),               // GS h 80  - barcode height 80 dots (~10mm)
    bytes(GS, 0x77, 0x02),               // GS w 2   - module width
    bytes(GS, 0x48, 0x02),               // GS H 2   - HRI below
    bytes(GS, 0x66, 0x00),               // GS f 0   - HRI font A
    bytes(GS, 0x6b, 0x43, barcodeData.length),  // GS k 67 n (Function B, EAN-13)
    barcodeData,
    bytes(LF),
    bytes(ESC, 0x61, 0x01),              // center
    encodeText(sku || ''),
    bytes(LF, LF, LF),                   // feed
  );
};

const buildLabelCode128 = ({ name, sku, barcode }) => {
  // Fallback for non-numeric barcodes (rare in this app but safe).
  const cleanName = truncate(name || '', 28);
  const data = encodeText(barcode || sku || '');
  return concat(
    bytes(ESC, 0x40),
    bytes(ESC, 0x61, 0x01),
    bytes(ESC, 0x21, 0x08),
    encodeText(cleanName),
    bytes(LF),
    bytes(ESC, 0x21, 0x00),
    bytes(GS, 0x68, 0x50),
    bytes(GS, 0x77, 0x02),
    bytes(GS, 0x48, 0x02),
    bytes(GS, 0x6b, 0x49, data.length + 2, 0x7b, 0x42),  // CODE128 function B, {B prefix
    data,
    bytes(LF),
    encodeText(sku || ''),
    bytes(LF, LF, LF),
  );
};

const truncate = (s, n) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

// ============ CONNECTION MANAGEMENT ============

let cachedDevice = null;
let cachedCharacteristic = null;

export const bluetoothAvailable = () =>
  typeof navigator !== 'undefined'
  && !!navigator.bluetooth
  && typeof navigator.bluetooth.requestDevice === 'function';

export const rememberedPrinterName = () => localStorage.getItem(STORAGE_KEY);

const rememberPrinter = (device) => {
  if (device?.name) localStorage.setItem(STORAGE_KEY, device.name);
};

export const forgetPrinter = () => {
  localStorage.removeItem(STORAGE_KEY);
  cachedDevice = null;
  cachedCharacteristic = null;
};

/**
 * Prompt the user to pick a Bluetooth printer. Chrome / Edge show the native
 * chooser. We filter by the standard ESC/POS service UUID plus common name
 * prefixes so the Xprinter appears immediately.
 */
export const requestPrinter = async () => {
  if (!bluetoothAvailable()) {
    throw new Error('Web Bluetooth is not available in this browser. Use the Print button instead.');
  }
  const device = await navigator.bluetooth.requestDevice({
    filters: [
      { services: [PRINTER_SERVICE_UUID] },
      { namePrefix: 'XP-' },
      { namePrefix: 'Xprinter' },
      { namePrefix: 'Printer' },
      { namePrefix: 'BT' },
    ],
    optionalServices: [PRINTER_SERVICE_UUID],
  });
  rememberPrinter(device);
  cachedDevice = device;
  cachedCharacteristic = null;
  return device;
};

const ensureConnected = async () => {
  if (cachedCharacteristic && cachedDevice?.gatt?.connected) {
    return cachedCharacteristic;
  }
  if (!cachedDevice) {
    // First-print flow: prompt the user to pick.
    cachedDevice = await requestPrinter();
  }
  const server = await cachedDevice.gatt.connect();
  const service = await server.getPrimaryService(PRINTER_SERVICE_UUID);
  cachedCharacteristic = await service.getCharacteristic(PRINTER_WRITE_UUID);
  cachedDevice.addEventListener('gattserverdisconnected', () => {
    cachedCharacteristic = null;
  });
  return cachedCharacteristic;
};

const writeChunked = async (characteristic, buffer) => {
  for (let offset = 0; offset < buffer.length; offset += CHUNK_SIZE) {
    const chunk = buffer.slice(offset, offset + CHUNK_SIZE);
    // writeValueWithoutResponse is faster; fall back to writeValue if not supported.
    if (characteristic.writeValueWithoutResponse) {
      await characteristic.writeValueWithoutResponse(chunk);
    } else {
      await characteristic.writeValue(chunk);
    }
    // Small delay to avoid buffer overrun on cheap BLE stacks.
    await new Promise((r) => setTimeout(r, 20));
  }
};

/**
 * Print one or more labels via Bluetooth. `items` is an array of
 * `{ name, sku, barcode }`.
 */
export const printLabelsBluetooth = async (items) => {
  const characteristic = await ensureConnected();
  for (const item of items) {
    const buffer = buildLabelCommands(item);
    await writeChunked(characteristic, buffer);
  }
};
